import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment/moment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GoBack from '../subsidiary/GoBack/GoBack';
import {
    fetchNotifications,
    selectNotifications,
    markAsReadLocally,
} from './notificationsSlice';

const NotificationsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const notifications = useSelector(selectNotifications);
    const isLoaded = useSelector((state) => state.notificationsSlice.isLoaded);
    const error = useSelector((state) => state.notificationsSlice.error);

    useEffect(() => {
        dispatch(fetchNotifications());
    }, [dispatch]);

    const handleNotificationClick = (notification) => {
        // Optimistically mark as read in the UI
        if (notification.id) {
            dispatch(markAsReadLocally(notification.id));
        }

        // If backend ever provides explicit link, prefer it
        if (notification.link) {
            navigate(notification.link);
            return;
        }

        // Fallback: navigate based on notification type
        switch (notification.type) {
            case 'ticket':
            case 'ticket_reply':
            case 'ticket_status':
                navigate('/tickets');
                break;
            case 'application':
            case 'application_status':
                navigate('/declaration');
                break;
            default:
                // For other types (e.g. news) stay on the page for now
                break;
        }
    };

    const renderContent = () => {
        if (!isLoaded) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ mt: 4 }}>
                    <Typography color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (!notifications.length) {
            return (
                <Box sx={{ mt: 4 }}>
                    <Typography color="text.secondary">
                        У вас пока нет уведомлений.
                    </Typography>
                </Box>
            );
        }

        return (
            <List>
                {notifications.map((notification) => {
                    const isRead = !!notification.is_read;
                    const createdAt = notification.created_at;
                    // Показываем текст сообщения, а не только тип,
                    // чтобы, например, было видно «Получен ответ на тикет».
                    const primaryText = notification.text || notification.title;
                    const secondaryLines = [];

                    if (createdAt) {
                        secondaryLines.push(
                            moment(createdAt).format('DD.MM.YYYY HH:mm')
                        );
                    }

                    return (
                        <ListItem
                            key={notification.id}
                            button
                            onClick={() => handleNotificationClick(notification)}
                            alignItems="flex-start"
                            secondaryAction={
                                <ArrowForwardIosIcon
                                    fontSize="small"
                                    style={{ color: '#BDBDBD' }}
                                />
                            }
                        >
                            <ListItemAvatar>
                                <Badge
                                    color="error"
                                    variant="dot"
                                    invisible={!!isRead}
                                    overlap="circular"
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: isRead ? '#E0E0E0' : '#EF2E25',
                                        }}
                                    >
                                        <NotificationsIcon fontSize="small" />
                                    </Avatar>
                                </Badge>
                            </ListItemAvatar>
                            <ListItemText
                                primary={primaryText}
                                secondary={secondaryLines.join(' • ')}
                            />
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    return (
        <section className="main">
            <GoBack text="Назад" />
            <div className="content-wrapper">
                <div className="page-title-container">
                    <h1 className="page-title">
                        Уведомления
                    </h1>
                </div>
                {renderContent()}
            </div>
        </section>
    );
};

export default NotificationsPage;

